/**
 * Is a logo pale and one-coloured (a cream or white wordmark, made for dark backgrounds)? Only such a logo is drawn
 * darkened on a light page — a full-colour logo (Hey Kiddo's badge) darkened becomes a black blot. Same rule as the
 * API's PDF renderer (engine/logoTone.ts): the visible pixels' mean lightness and how many of them carry colour.
 */
export function paleFromPixels(data: Uint8ClampedArray | Uint8Array): boolean | null {
  let n = 0; let lum = 0; let colourful = 0;
  for (let i = 0; i + 3 < data.length; i += 4) {
    if (data[i + 3] < 40) continue;
    const r = data[i], g = data[i + 1], b = data[i + 2];
    n++; lum += (0.2126 * r + 0.7152 * g + 0.0722 * b) / 255;
    if (Math.max(r, g, b) - Math.min(r, g, b) > 50) colourful++;
  }
  if (!n) return null;
  return lum / n > 0.75 && colourful / n < 0.1;
}

/**
 * Does the logo carry its own background (an opaque rectangle, e.g. a JPG or a PNG saved on black)? Such a logo cannot
 * be turned into a white silhouette on a dark page — the whole rectangle becomes a white box — so it is shown as it is.
 */
export function opaqueFromPixels(data: Uint8ClampedArray | Uint8Array): boolean {
  let n = 0; let opaque = 0;
  for (let i = 3; i < data.length; i += 4) { n++; if (data[i] >= 245) opaque++; }
  return n > 0 && opaque / n > 0.97;
}

export interface LogoTraits { pale: boolean | null; opaque: boolean }
const verdicts = new Map<string, Promise<LogoTraits | null>>();
/** Is the logo pale (see paleFromPixels)? null when its pixels cannot be read. */
export function logoIsPale(src: string): Promise<boolean | null> { return logoTraits(src).then((t) => (t ? t.pale : null)); }
/** Read the logo's pixels in the browser (a separate CORS load, so the shown image never depends on it); null when it cannot be read. */
export function logoTraits(src: string): Promise<LogoTraits | null> {
  if (!verdicts.has(src)) verdicts.set(src, new Promise((resolve) => {
    const img = new Image(); img.crossOrigin = 'anonymous';
    img.onload = () => {
      try {
        const s = 48 / Math.max(img.naturalWidth || 48, img.naturalHeight || 48);
        const c = document.createElement('canvas'); c.width = Math.max(1, Math.round((img.naturalWidth || 48) * s)); c.height = Math.max(1, Math.round((img.naturalHeight || 48) * s));
        const ctx = c.getContext('2d'); if (!ctx) return resolve(null);
        ctx.drawImage(img, 0, 0, c.width, c.height);
        const data = ctx.getImageData(0, 0, c.width, c.height).data;
        resolve({ pale: paleFromPixels(data), opaque: opaqueFromPixels(data) });
      } catch { resolve(null); } // a host without CORS: no verdict
    };
    img.onerror = () => resolve(null);
    img.src = src;
  }));
  return verdicts.get(src)!;
}
